
import { supabase } from '@/integrations/supabase/client';
import { getActiveEntitiesStats } from './activeEntitiesService';
import { getMonthlyActiveCouriers, getMonthlyBikeUtilization } from './monthlyDataService';
import { getMonthlyNewCustomers, getMonthlyRepeatCustomers } from './customerAnalyticsService';

export interface MonthlyMetric {
  year: number;
  month: number;
  value: number;
  previousValue?: number;
  growth?: number;
}

/**
 * Получает месячные метрики с использованием динамических данных
 */
export const getMonthlyMetrics = async (monthsCount: number = 12) => {
  try {
    console.log(`Загрузка динамических месячных метрик за ${monthsCount} месяцев...`);
    
    // Получаем динамические данные по месяцам
    const [bikeUtilizationMetrics, activeCouriersMetrics, revenueMetrics, newCustomersMetrics, repeatCustomersMetrics] = await Promise.all([
      getMonthlyBikeUtilization(monthsCount),
      getMonthlyActiveCouriers(monthsCount),
      getMonthlyRevenueMetrics(monthsCount),
      getMonthlyNewCustomers(monthsCount),
      getMonthlyRepeatCustomers(monthsCount)
    ]);
    
    console.log('Динамические месячные метрики:', {
      bikeUtilization: bikeUtilizationMetrics.length,
      activeCouriers: activeCouriersMetrics.length,
      revenue: revenueMetrics.length,
      newCustomers: newCustomersMetrics.length,
      repeatCustomers: repeatCustomersMetrics.length
    });
    
    return {
      bikeUtilization: bikeUtilizationMetrics,
      revenue: revenueMetrics,
      activeCouriers: activeCouriersMetrics,
      newCustomers: newCustomersMetrics,
      repeatCustomers: repeatCustomersMetrics
    };
  } catch (error) {
    console.error('Ошибка при получении динамических месячных метрик:', error);
    return {
      bikeUtilization: [],
      revenue: [],
      activeCouriers: [],
      newCustomers: [],
      repeatCustomers: []
    };
  }
};

/**
 * Получает данные о доходах по месяцам
 */
const getMonthlyRevenueMetrics = async (monthsCount: number): Promise<MonthlyMetric[]> => {
  try {
    // Получаем платежи за последние месяцы
    const { data: paymentsData, error } = await supabase
      .from('payments')
      .select('amount, payment_date, status')
      .in('status', ['paid', 'completed', 'success'])
      .gte('payment_date', new Date(Date.now() - monthsCount * 30 * 24 * 60 * 60 * 1000).toISOString())
      .order('payment_date', { ascending: false });
    
    if (error || !paymentsData) {
      console.error('Ошибка при получении данных о платежах:', error);
      return [];
    }
    
    // Группируем платежи по месяцам
    const monthlyRevenue = new Map<string, number>();
    
    paymentsData.forEach(payment => {
      if (payment.payment_date && payment.amount) {
        const date = new Date(payment.payment_date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const amount = typeof payment.amount === 'number' ? payment.amount : parseFloat(payment.amount);
        
        if (!isNaN(amount)) {
          monthlyRevenue.set(monthKey, (monthlyRevenue.get(monthKey) || 0) + amount);
        }
      }
    });
    
    // Преобразуем в массив метрик
    const metrics: MonthlyMetric[] = [];
    monthlyRevenue.forEach((value, monthKey) => {
      const [year, month] = monthKey.split('-').map(Number);
      metrics.push({
        year,
        month,
        value: Math.round(value),
        growth: 0 // Рассчитаем позже при наличии предыдущих данных
      });
    });
    
    // Сортируем по дате
    metrics.sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
    
    // Рассчитываем рост по сравнению с предыдущим месяцем
    for (let i = 1; i < metrics.length; i++) {
      const current = metrics[i].value;
      const previous = metrics[i - 1].value;
      metrics[i].previousValue = previous;
      if (previous > 0) {
        metrics[i].growth = Math.round(((current - previous) / previous) * 100);
      }
    }
    
    return metrics;
  } catch (error) {
    console.error('Ошибка при получении месячных данных о доходах:', error);
    return [];
  }
};
